import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Box,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import api from "../../utils/api";

const SuggestServiceModal = ({ open, onClose, categoryId, language }) => {
  const [serviceName, setServiceName] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(language === "ar" ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image size must be less than 5MB");
        return;
      }
      
      if (!file.type.startsWith("image/")) {
        setError(language === "ar" ? "يجب اختيار صورة فقط" : "Please select an image file only");
        return;
      }

      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
      setError("");
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async () => {
    if (!serviceName.trim()) {
      setError(language === "ar" ? "يرجى إدخال اسم الخدمة" : "Please enter service name");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("name[ar]", serviceName);
      formData.append("name[en]", serviceName);
      formData.append("category", categoryId);
      
      if (description) {
        formData.append("description[ar]", description);
        formData.append("description[en]", description);
      }
      
      if (photo) {
        formData.append("photo", photo);
      }

      await api.post("/recommended-services", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess(true);
      
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error("Error submitting recommended service:", err);
      setError(
        err.response?.data?.message || 
        (language === "ar" 
          ? "حدث خطأ أثناء إرسال الاقتراح. يرجى المحاولة مرة أخرى" 
          : "An error occurred while submitting your suggestion. Please try again")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setServiceName("");
      setDescription("");
      setPhoto(null);
      setPhotoPreview(null);
      setError("");
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          direction: language === "ar" ? "rtl" : "ltr",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          pb: 1,
        }}
      >
        {language === "ar" ? "اقترح خدمة غير موجودة" : "Suggest a Service"}
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {language === "ar"
              ? "تم إرسال اقتراحك بنجاح! شكراً لك"
              : "Your suggestion has been submitted successfully! Thank you"}
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              placeholder={
                language === "ar"
                  ? "اسم الخدمة"
                  : "Service Name"
              }
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              disabled={loading}
              required
              sx={{ mb: 3 }}
              InputLabelProps={{
                sx: { 
                  right: language === "ar" ? 24 : "auto", 
                  left: language === "ar" ? "auto" : 0,
                  transformOrigin: language === "ar" ? "top right" : "top left",
                },
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={
                language === "ar"
                  ? "وصف الخدمة (اختياري)"
                  : "Service Description (Optional)"
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
              InputLabelProps={{
                sx: { 
                  right: language === "ar" ? 24 : "auto", 
                  left: language === "ar" ? "auto" : 0,
                  transformOrigin: language === "ar" ? "top right" : "top left",
                },
              }}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                {language === "ar" ? "صورة الخدمة (اختياري)" : "Service Photo (Optional)"}
              </Typography>
              
              {photoPreview ? (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: 200,
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "2px solid #e0e0e0",
                  }}
                >
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <IconButton
                    onClick={handleRemovePhoto}
                    disabled={loading}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={language === "ar" ? null : <CloudUploadIcon />}
                  endIcon={language === "ar" ? <CloudUploadIcon /> : null}
                  disabled={loading}
                  fullWidth
                  sx={{
                    height: 100,
                    borderStyle: "dashed",
                    borderWidth: 2,
                    textTransform: "none",
                    flexDirection: language === "ar" ? "row-reverse" : "row",
                    gap: 1,
                  }}
                >
                  {language === "ar" ? "رفع صورة" : "Upload Photo"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </Button>
              )}
              
              <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                {language === "ar"
                  ? "الحد الأقصى: 5 ميجابايت، الصيغ المدعومة: JPG, PNG, GIF"
                  : "Max size: 5MB, Supported formats: JPG, PNG, GIF"}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading} color="inherit">
            {language === "ar" ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !serviceName.trim()}
            variant="contained"
            color="primary"
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading
              ? (language === "ar" ? "جاري الإرسال..." : "Submitting...")
              : (language === "ar" ? "إرسال" : "Submit")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SuggestServiceModal;